/**
 * UpscaleVaryTab Component
 *
 * Allows users to upscale images or create variations using Fooocus-API.
 */

import { useState } from 'react';
import { Sparkles, Wand2 } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { ImageUploader } from '../ImageUploader';
import { ImageModelSelector } from '../ImageModelSelector';
import { StyleSelector } from '../StyleSelector';
import { ImageResultDisplay } from '../ImageResultDisplay';
import { imageGenerationAPI } from '@/services/imageGenerationAPI';
import type { UpscaleVaryMethod, ImageResult } from '@/types';

interface MethodOption {
  value: UpscaleVaryMethod;
  category: 'upscale' | 'vary';
  icon: typeof Sparkles;
}

const METHODS: MethodOption[] = [
  { value: 'Upscale (1.5x)', category: 'upscale', icon: Sparkles },
  { value: 'Upscale (2x)', category: 'upscale', icon: Sparkles },
  { value: 'Upscale (Fast 2x)', category: 'upscale', icon: Sparkles },
  { value: 'Vary (Subtle)', category: 'vary', icon: Wand2 },
  { value: 'Vary (Strong)', category: 'vary', icon: Wand2 },
];

export function UpscaleVaryTab() {
  const { t } = useTranslation();

  // Form state
  const [imageBase64, setImageBase64] = useState<string>('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [method, setMethod] = useState<UpscaleVaryMethod>('Upscale (2x)');
  const [model, setModel] = useState<string | undefined>();
  const [prompt, setPrompt] = useState('');
  const [selectedStyles, setSelectedStyles] = useState<string[]>([]);

  // UI state
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    images: ImageResult[];
    processingTime: number;
  } | null>(null);

  const handleImageSelect = (base64: string, file: File) => {
    setImageBase64(base64);
    setImageFile(file);
    setError(null);
  };

  const handleRemoveImage = () => {
    setImageBase64('');
    setImageFile(null);
  };

  const handleProcess = async () => {
    if (!imageBase64) {
      setError(
        t('image.upscaleVary.errors.noImage', {
          defaultValue: 'Please upload an image first',
        })
      );
      return;
    }

    setIsProcessing(true);
    setError(null);
    setResult(null);

    try {
      const response = await imageGenerationAPI.upscaleVaryImage({
        image: imageBase64,
        method,
        prompt: prompt.trim() || undefined,
        styles: selectedStyles.length > 0 ? selectedStyles : undefined,
        async_process: false,
        model,
      });

      if (response.success && response.data) {
        // Defensive: ensure images is always an array
        const images = Array.isArray(response.data.images) ? response.data.images : [];
        if (images.length > 0) {
          setResult({
            images,
            processingTime: response.data.processing_time_seconds || 0,
          });
        } else {
          setError(
            t('image.upscaleVary.errors.noResult', {
              defaultValue: 'No images were generated',
            })
          );
        }
      } else {
        setError(
          response.error ||
            t('image.upscaleVary.errors.processingFailed', {
              defaultValue: 'Failed to process image',
            })
        );
      }
    } catch {
      setError(
        t('image.upscaleVary.errors.networkError', {
          defaultValue: 'Network error. Please try again.',
        })
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setResult(null);
    setError(null);
  };

  const isVariationMethod = method.startsWith('Vary');
  const canProcess = imageBase64 && !isProcessing;

  // If we have results, show them
  if (result) {
    return (
      <ImageResultDisplay
        images={result.images}
        processingTime={result.processingTime}
        onReset={handleReset}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white">
          {t('image.upscaleVary.title', {
            defaultValue: 'Upscale & Vary',
          })}
        </h2>
        <p className="text-white/60 mt-1">
          {t('image.upscaleVary.description', {
            defaultValue: 'Enhance your images with upscaling or create creative variations',
          })}
        </p>
      </div>

      {/* Image Upload */}
      <div>
        <label className="form-label">
          {t('image.upscaleVary.uploadPrompt', {
            defaultValue: 'Upload an image to enhance',
          })}
        </label>
        <ImageUploader
          onImageSelect={handleImageSelect}
          onRemove={handleRemoveImage}
          disabled={isProcessing}
          maxSizeMB={10}
          previewUrl={imageFile ? URL.createObjectURL(imageFile) : undefined}
        />
      </div>

      {/* Method Selection */}
      <div>
        <label className="form-label">
          {t('image.upscaleVary.method', {
            defaultValue: 'Processing Method',
          })}
        </label>

        <div className="provider-grid">
          {METHODS.map((methodOption) => {
            const Icon = methodOption.icon;
            const isSelected = method === methodOption.value;
            const methodKey = methodOption.value
              .toLowerCase()
              .replace(/[()]/g, '')
              .replace(/\s+/g, '_');

            return (
              <button
                key={methodOption.value}
                type="button"
                onClick={() => setMethod(methodOption.value)}
                disabled={isProcessing}
                className={`
                  flex flex-col items-start p-4 rounded-lg border-2 transition-all
                  ${
                    isSelected
                      ? 'border-primary-500 bg-primary-500/20'
                      : 'border-secondary-700 hover:border-primary-500/40 hover:bg-white/5'
                  }
                  ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
              >
                <div className="flex items-center gap-2 w-full">
                  <Icon
                    className={`h-5 w-5 ${isSelected ? 'text-primary-400' : 'text-white/50'}`}
                  />
                  <span className={`font-medium ${isSelected ? 'text-white' : 'text-white/80'}`}>
                    {t(`image.upscaleVary.methods.${methodKey}`, {
                      defaultValue: methodOption.value,
                    })}
                  </span>
                </div>
                <p className="text-xs text-white/50 mt-2">
                  {t(`image.upscaleVary.methodDescriptions.${methodKey}`, {
                    defaultValue: '',
                  })}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Checkpoint Model Selector */}
      <ImageModelSelector
        value={model}
        onChange={setModel}
        provider="fooocus"
        disabled={isProcessing}
      />

      {/* Optional Prompt (for variations) */}
      {isVariationMethod && (
        <div>
          <label htmlFor="prompt" className="form-label">
            {t('image.upscaleVary.optionalPrompt', {
              defaultValue: 'Optional: Add a prompt to guide variations',
            })}{' '}
            <span className="text-white/40 font-normal">
              ({t('common.optional', { defaultValue: 'optional' })})
            </span>
          </label>
          <textarea
            id="prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            disabled={isProcessing}
            placeholder={t('image.prompt.placeholder', {
              defaultValue: 'Describe the image you want to create...',
            })}
            rows={3}
            className="input resize-none"
          />
        </div>
      )}

      {/* Optional Styles */}
      {isVariationMethod && (
        <StyleSelector
          selectedStyles={selectedStyles}
          onChange={setSelectedStyles}
          disabled={isProcessing}
          maxStyles={5}
        />
      )}

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-sm text-red-300">{error}</p>
        </div>
      )}

      {/* Process Button */}
      <button
        type="button"
        onClick={handleProcess}
        disabled={!canProcess}
        className={`
          w-full py-3 px-6 rounded-lg font-medium transition-all
          flex items-center justify-center gap-2
          ${
            canProcess
              ? 'bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800'
              : 'bg-secondary-700 text-white/40 cursor-not-allowed'
          }
        `}
      >
        {isProcessing ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
            {t('image.upscaleVary.processing', {
              defaultValue: 'Processing...',
            })}
          </>
        ) : (
          <>
            <Sparkles className="h-5 w-5" />
            {t('image.upscaleVary.process', {
              defaultValue: 'Process Image',
            })}
          </>
        )}
      </button>
    </div>
  );
}

export default UpscaleVaryTab;
